import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';

export type Match2ButtonBadgeState =
  | 'count'
  | 'tutorial'
  | 'add'
  | 'selected'
  | 'active'
  | 'locked';

export class Match2ButtonBadge extends View {
  private badge: ImageScaleView;
  private badgeIcon: ImageScaleView;
  private badgeText: LangBitmapFontTextView;
  private lockIcon: ImageScaleView;
  private infiniteIcon: ImageScaleView;
  private baseScale: number;
  private noAnimation: boolean;
  private count = 0;
  private state = 'count';

  constructor(
    opts: Omit<
      ViewOpts,
      'width' | 'height' | 'centerAnchor' | 'centerOnOrigin'
    > & { noAnimation?: boolean },
  ) {
    const width = 50;
    const height = 50;

    super({
      width,
      height,
      centerOnOrigin: true,
      centerAnchor: true,
      ...opts,
    });

    this.baseScale = opts.scale ?? 1;
    this.noAnimation = !!opts.noAnimation;

    this.badge = new ImageScaleView({
      superview: this,
      width: 44,
      height: 44,
      scaleMethod: 'contain',
      image: `assets/ui/match2/common/badge_orange.png`,
      centerAnchor: true,
    });

    this.badgeText = new LangBitmapFontTextView({
      superview: this.badge,
      font: 'Body',
      color: '#FFFFFF',
      size: 20,
      width: 30,
      height: 30,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      localeText: () => `X`,
      x: this.badge.style.width * 0.5 - 2,
      y: this.badge.style.height * 0.5 - 4,
    });

    const x = this.badge.style.width * 0.5;
    const y = this.badge.style.height * 0.5 - 4;

    this.badgeIcon = new ImageScaleView({
      superview: this.badge,
      width: 27,
      height: 27,
      image: `assets/ui/match2/common/icon_add.png`,
      centerOnOrigin: true,
      centerAnchor: true,
      x,
      y,
    });

    this.lockIcon = new ImageScaleView({
      superview: this,
      width: 38,
      height: 44,
      image: `assets/ui/accessories/icons/lock_icon.png`,
      centerOnOrigin: true,
      centerAnchor: true,
      x,
      y,
    });

    this.infiniteIcon = new ImageScaleView({
      superview: this,
      width: 28,
      height: 28,
      image: `assets/ui/match2/common/icon_infinite_white.png`,
      centerOnOrigin: true,
      centerAnchor: true,
      scaleMethod: 'contain',
      x,
      y,
    });
  }

  public async setState(state: Match2ButtonBadgeState) {
    this.state = state;
    this.refreshState();
    if (!this.noAnimation) {
      animate(this)
        .clear()
        .now({ scale: 0 })
        .then({ scale: this.baseScale }, 150);
    }
  }

  public setCount(count: number) {
    this.count = count;
    this.refreshState();
  }

  private refreshState() {
    const state = this.state;
    const infinite = this.count === Infinity;
    const isLocked = state === 'locked';
    const showText =
      state === 'count' || state === 'active' || state === 'tutorial';
    this.badgeText.updateOpts({
      localeText: () =>
        state === 'tutorial' ? i18n('basic.free') : this.count.toString(),
      x: this.badge.style.width * 0.5 - (state === 'tutorial' ? 1 : 2),
    });
    this.lockIcon.style.visible = isLocked;
    this.badge.style.visible = !isLocked;
    this.badgeIcon.style.visible = state === 'add' || state === 'selected';
    this.badgeText.style.visible = showText && !infinite;
    this.infiniteIcon.style.visible = showText && infinite;
    if (!isLocked)
      this.badge.setImage(
        `assets/ui/match2/common/badge_${
          this.badgeText.style.visible ? 'orange' : 'green'
        }.png`,
      );
    if (this.badgeIcon.style.visible)
      this.badgeIcon.setImage(
        `assets/ui/match2/common/icon_${
          state === 'selected' ? 'tick' : 'add'
        }.png`,
      );
  }
}
